import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, inr, BACKEND, imgUrl, STATUS_LABEL, STATUS_FLOW, PAY_METHOD, PAY_STATUS, REFUND_STATUS } from "@/lib/api";
import { Panel, Btn, Pill, statusTone, payTone } from "@/admin/ui";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [note, setNote] = useState("");
  const [custNote, setCustNote] = useState("");
  const [refRef, setRefRef] = useState("");
  const [trustNext, setTrustNext] = useState(true);

  useEffect(() => { adminApi.get(`/orders/${id}`).then((r) => { setO(r.data); setCustNote(r.data.customer_support_note || ""); }); }, [id]);

  const act = async (fn, msg) => {
    try { const r = await fn(); setO(r.data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!o) return <p className="text-stone-500">Loading…</p>;

  const closed = ["cancelled", "delivered"].includes(o.status);
  const next = STATUS_FLOW[STATUS_FLOW.indexOf(o.status) + 1];
  const setStatus = (s) => act(() => adminApi.post(`/orders/${id}/status`, { status: s }), `Marked ${STATUS_LABEL[s]}`);
  const pay = (action) => act(() => adminApi.post(`/orders/${id}/payment`, { action }), "Payment updated");

  return (
    <div className="space-y-4">
      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-primary" data-testid="admin-back-orders"><ArrowLeft className="w-4 h-4" /> Orders</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-primary font-semibold" data-testid="admin-order-number">{o.order_number}</h1>
        <Pill tone={statusTone(o.status)} testId="admin-order-status">{STATUS_LABEL[o.status]}</Pill>
        <span className="text-sm text-stone-500">{o.delivery_date} · {o.slot_label}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Link to={`/admin/slips?id=${o.id}`}><Btn variant="outline" data-testid="print-slip-btn">Print slip</Btn></Link>
          {!closed && next && o.cod_approval !== "pending" && <Btn onClick={() => setStatus(next)} data-testid="advance-status-btn">Mark {STATUS_LABEL[next]}</Btn>}
          {!closed && <Btn variant="outline" onClick={() => window.confirm("Cancel this order? Stock will be returned.") && setStatus("cancelled")} data-testid="admin-cancel-order-btn">Cancel order</Btn>}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
        <div className="space-y-4">
          <Panel title="Items — confirm stock" testId="admin-items-panel">
            <div className="divide-y divide-stone-100">
              {o.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 py-2" data-testid={`admin-item-${i}`}>
                  <img src={imgUrl(it.image)} alt="" className="w-10 h-10 rounded object-cover" />
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${it.status === "unavailable" ? "line-through text-stone-400" : ""}`}>{it.name} <span className="text-stone-400 font-normal">({it.unit})</span>{it.cut && <span className="ml-1.5" data-testid={`admin-item-cut-${i}`}><Pill tone="orange">{it.cut}{it.cut_charge ? ` +${inr(it.cut_charge)}` : ""}</Pill></span>}</p>
                    <p className="text-xs text-stone-500">{inr(it.price)} × {it.qty} = {inr(it.line_total)}</p>
                  </div>
                  <Pill tone={it.status === "unavailable" ? "orange" : it.status === "confirmed" ? "green" : "grey"}>{it.status}</Pill>
                  {!closed && (
                    <Btn variant={it.status === "unavailable" ? "outline" : "ghost"} onClick={() => act(() => adminApi.post(`/orders/${id}/items/${i}`, { unavailable: it.status !== "unavailable" }), "Item updated")} data-testid={`admin-item-toggle-${i}`}>
                      {it.status === "unavailable" ? "Restore" : "Mark unavailable"}
                    </Btn>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-stone-200 mt-2 pt-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></div>
              <div className="flex justify-between"><span>Delivery fee</span><span data-testid="admin-delivery-fee">{inr(o.delivery_fee)}</span></div>
              <div className="flex justify-between text-accent"><span>Refund (unavailable items)</span><span data-testid="admin-refund-total">− {inr(o.refund_total)}</span></div>
              <div className="flex justify-between font-extrabold text-primary text-base"><span>{o.payment_method === "bank_transfer" ? "Net amount" : "Collect on delivery"}</span><span data-testid="admin-amount-due">{inr(o.amount_due)}</span></div>
            </div>
          </Panel>

          <Panel title="Refund" testId="admin-refund-panel">
            <p className="text-sm">Status: <b data-testid="admin-refund-status">{REFUND_STATUS[o.refund_status]}</b> {o.refund_reference && <span className="text-stone-500">· Ref {o.refund_reference}</span>}</p>
            {o.refund_status === "adjusted" && <p className="text-xs text-stone-500 mt-1">Pay-on-delivery order — rider collects the reduced amount; no transfer needed.</p>}
            {o.refund_status === "pending" && (
              <div className="flex gap-2 mt-3">
                <Input placeholder="Refund UTR / reference" value={refRef} onChange={(e) => setRefRef(e.target.value)} className="h-9" data-testid="refund-reference-input" />
                <Btn onClick={() => act(() => adminApi.post(`/orders/${id}/refund-complete`, { reference: refRef }), "Refund marked complete")} data-testid="refund-complete-btn">Mark refunded</Btn>
              </div>
            )}
          </Panel>

          <Panel title="Support notes" testId="admin-notes-panel">
            <div className="space-y-2 mb-3">
              {o.admin_notes.map((n, i) => (
                <div key={i} className="text-sm bg-[#F4F5F5] rounded p-2" data-testid={`admin-note-${i}`}><p>{n.text}</p><p className="text-[11px] text-stone-400 mt-0.5">{new Date(n.at).toLocaleString("en-IN")} · {n.by}</p></div>
              ))}
              {o.admin_notes.length === 0 && <p className="text-sm text-stone-400">No notes yet.</p>}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (note.trim()) act(() => adminApi.post(`/orders/${id}/notes`, { text: note }), "Note added").then(() => setNote("")); }} className="flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Customer called, deliver after 6pm" className="h-9" data-testid="admin-note-input" />
              <Btn data-testid="admin-note-add-btn">Add</Btn>
            </form>
          </Panel>

          <Panel title="History" testId="admin-history-panel">
            <ol className="text-xs space-y-1 text-stone-600">
              {o.history.map((h, i) => <li key={i}><b>{h.status}</b> · {new Date(h.at).toLocaleString("en-IN")} · {h.by}</li>)}
            </ol>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Customer" testId="admin-customer-panel">
            <p className="font-bold">{o.name}</p>
            <p className="text-sm"><a href={`tel:+91${o.phone}`} className="text-primary underline" data-testid="admin-customer-phone">+91 {o.phone}</a> · {o.customer_order_count} orders</p>
            <label className="flex items-center gap-2 text-sm mt-2" data-testid="cod-trusted-label">
              <Switch checked={!!o.customer_cod_trusted} onCheckedChange={(v) => adminApi.put(`/customers/${o.customer_id}/cod-trust`, { trusted: v }).then(() => { setO({ ...o, customer_cod_trusted: v }); toast.success(v ? "Customer trusted for COD" : "COD trust removed"); })} data-testid="cod-trusted-switch" />
              Trusted for COD
            </label>
            <p className="text-sm text-stone-600 mt-2">{o.address.line1}{o.address.landmark ? `, ${o.address.landmark}` : ""}</p>
            <p className="text-sm text-stone-600">{o.area_name} · {o.pincode}</p>
            {o.customer_note && <p className="text-sm mt-2 bg-accent/10 text-accent rounded p-2" data-testid="admin-customer-order-note">Rider note: {o.customer_note}</p>}
            <p className="text-[11px] uppercase font-bold text-stone-500 mt-4 mb-1">Customer support note (all orders)</p>
            <Textarea value={custNote} onChange={(e) => setCustNote(e.target.value)} className="min-h-[70px] text-sm" data-testid="customer-support-note-input" />
            <Btn variant="outline" className="mt-2" onClick={() => adminApi.put(`/customers/${o.customer_id}/note`, { note: custNote }).then(() => toast.success("Saved"))} data-testid="customer-support-note-save">Save note</Btn>
          </Panel>

          <Panel title="WhatsApp updates" testId="admin-whatsapp-panel"
            action={["confirmed", "out_for_delivery", "delivered"].includes(o.status) && (
              <Btn variant="outline" onClick={() => act(() => adminApi.post(`/orders/${id}/notify`), "WhatsApp update sent")} data-testid="whatsapp-resend-btn">Resend current</Btn>
            )}>
            <p className="text-xs text-stone-500 mb-2" data-testid="whatsapp-optin">{o.whatsapp_updates === false ? "Customer opted out — only manual resend sends a message." : "Sent automatically on Confirmed, Out for delivery and Delivered."}</p>
            {(o.notifications || []).length === 0 ? <p className="text-sm text-stone-400">No messages yet.</p> : (
              <ul className="space-y-1.5 text-sm">
                {o.notifications.map((n, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`whatsapp-log-${i}`}>
                    <Pill tone={n.status === "sent" ? "green" : n.status === "failed" ? "orange" : "grey"}>{n.status}</Pill>
                    <span>{STATUS_LABEL[n.event]}</span>
                    <span className="text-[11px] text-stone-400 ml-auto">{new Date(n.at).toLocaleTimeString("en-IN")}</span>
                    {n.error && <span className="basis-full text-[11px] text-stone-500">{n.error}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {o.cod_approval === "pending" && o.status !== "cancelled" && (
            <Panel title="COD approval needed" className="border-accent" testId="cod-approval-panel">
              <p className="text-sm text-stone-600">New customer paying cash on delivery. Call <b>+91 {o.phone}</b> to confirm, then approve.</p>
              <label className="flex items-center gap-2 text-sm mt-3">
                <input type="checkbox" checked={trustNext} onChange={(e) => setTrustNext(e.target.checked)} data-testid="cod-trust-checkbox" /> Also trust this customer for future COD
              </label>
              <div className="flex gap-2 mt-3">
                <Btn onClick={() => act(() => adminApi.post(`/orders/${id}/cod-approval`, { approve: true, trust_customer: trustNext }), "COD order approved")} data-testid="cod-approve-btn">Approve COD</Btn>
                <Btn variant="outline" onClick={() => window.confirm("Reject and cancel this order?") && act(() => adminApi.post(`/orders/${id}/cod-approval`, { approve: false }), "Order rejected")} data-testid="cod-reject-btn">Reject</Btn>
              </div>
            </Panel>
          )}

          <Panel title="Payment" testId="admin-payment-panel">
            <p className="text-sm font-semibold">{PAY_METHOD[o.payment_method]}</p>
            <div className="mt-1"><Pill tone={payTone(o.payment_status)} testId="admin-payment-status">{PAY_STATUS[o.payment_status]}</Pill></div>
            {o.payment_ref && <p className="text-sm mt-2">UTR: <b data-testid="admin-payment-utr">{o.payment_ref}</b></p>}
            {o.payment_proof_path && (
              <a href={`${BACKEND}/api/files/${o.payment_proof_path}`} target="_blank" rel="noreferrer" data-testid="admin-payment-proof-link">
                <img src={`${BACKEND}/api/files/${o.payment_proof_path}`} alt="Payment proof" className="mt-2 rounded border border-stone-200 max-h-64 object-contain" />
              </a>
            )}
            {o.payment_method === "bank_transfer" && o.payment_status !== "verified" && o.status !== "cancelled" && (
              <div className="flex gap-2 mt-3">
                <Btn onClick={() => pay("verify")} data-testid="payment-verify-btn">Verify payment</Btn>
                <Btn variant="outline" onClick={() => pay("reject")} data-testid="payment-reject-btn">Reject</Btn>
              </div>
            )}
            {o.payment_method === "bank_transfer" && o.payment_status === "awaiting_proof" && <p className="text-xs text-accent mt-2">No proof yet — call the customer before packing.</p>}
            {o.payment_method !== "bank_transfer" && o.payment_status !== "paid" && o.status !== "cancelled" && (
              <Btn variant="outline" className="mt-3" onClick={() => pay("mark_paid")} data-testid="payment-mark-paid-btn">Mark collected</Btn>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
